// frontend/src/pages/Contribute.tsx
import { UploadCloud, Share2, CheckCircle, Lock, Info } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../../auth/AuthContext";
import {
  fetchPendingContributions,
  type Contribution,
} from "../../api/adminContributions";
import ContributionCard from "../admin/components/ContributionCard";

/* ── Step Progress ─────────────────────────────────────────────────── */
const STEPS = [
  { label: "Submit Details", desc: "Fill in forum info" },
  { label: "Verification", desc: "Analyst review" },
  { label: "Approval", desc: "Goes live" },
];

function StepProgress({ current = 0 }: { current?: number }) {
  return (
    <div className="ctrb-steps">
      {STEPS.map((step, idx) => {
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={idx} className="ctrb-step-item">
            <div className={`ctrb-step-circle${done ? " ctrb-step-done" : active ? " ctrb-step-active" : " ctrb-step-idle"}`}>
              {done ? (
                <CheckCircle size={14} />
              ) : (
                <span>{idx + 1}</span>
              )}
              {active && <span className="ctrb-step-pulse" />}
            </div>
            <div className="ctrb-step-info">
              <span className={`ctrb-step-label${active ? " ctrb-step-label-active" : done ? " ctrb-step-label-done" : ""}`}>
                {step.label}
              </span>
              <span className="ctrb-step-desc">{step.desc}</span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`ctrb-step-line${done ? " ctrb-step-line-done" : ""}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── InfoCard ─────────────────────────────────────────────────────── */
function InfoCard({ title, icon, items, color = "#6366F1" }: {
  title: string;
  icon: React.ReactNode;
  items: string[];
  color?: string;
}) {
  return (
    <div className="ctrb-info-card">
      <div className="ctrb-info-header">
        <div className="ctrb-info-icon" style={{ background: color + "22", color }}>
          {icon}
        </div>
        <h3 className="ctrb-info-title">{title}</h3>
      </div>
      <ul className="ctrb-info-list">
        {items.map((item, i) => (
          <li key={i} className="ctrb-info-item">
            <span className="ctrb-info-dot" style={{ background: color }} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────── */
export default function Contribute() {
  const { user } = useAuth();

  // Admin state
  const [adminItems, setAdminItems] = useState<Contribution[]>([]);
  const [adminLoading, setAdminLoading] = useState(true);
  const [showReviewPanel, setShowReviewPanel] = useState(false);

  // Form state
  const [forumName, setForumName] = useState("");
  const [primaryLink, setPrimaryLink] = useState("");
  const [mirrors, setMirrors] = useState("");
  const [notes, setNotes] = useState("");
  const [dragOver, setDragOver] = useState(false);

  async function loadAdminItems() {
    setAdminLoading(true);
    try {
      const data = await fetchPendingContributions();
      setAdminItems(data);
    } catch (err) {
      console.error("Failed to load contributions", err);
    } finally {
      setAdminLoading(false);
    }
  }

  useEffect(() => {
    if (user?.role === "admin") loadAdminItems();
  }, [user?.role]);

  const isOnionUrl = (url: string) => /^https?:\/\/.+\.onion/.test(url.trim());

  const errors = {
    forumName: forumName.trim() === "",
    primaryLink: primaryLink.trim() === "" || !isOnionUrl(primaryLink),
  };

  const isFormValid = !errors.forumName && !errors.primaryLink;

  /* ── Admin Review Panel ── */
  if (user?.role === "admin" && showReviewPanel) {
    return (
      <div className="ctrb-page">
        <div className="ctrb-review-header">
          <div>
            <span className="ctrb-status-badge">Review Pending</span>
            <h1 className="ctrb-review-title">Contribution Review</h1>
            <p className="ctrb-review-sub">
              Review and approve pending darknet forum submissions.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="ctrb-count-chip">{adminItems.length} Pending</span>
            <button
              className="ctrb-hide-btn"
              onClick={() => setShowReviewPanel(false)}
            >
              ← Back to Submit
            </button>
          </div>
        </div>

        {adminLoading ? (
          <div className="ctrb-loading-txt">Loading contributions…</div>
        ) : adminItems.length === 0 ? (
          <div className="ctrb-empty-review">
            <CheckCircle size={32} style={{ color: "#22c55e" }} />
            <p>All clear — no pending contributions.</p>
          </div>
        ) : (
          <div className="ctrb-review-grid">
            {adminItems.map((c) => (
              <ContributionCard
                key={c._id}
                contribution={c}
                onAction={loadAdminItems}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Submit Form ── */
  return (
    <div className="ctrb-page">

      {/* ── Page header ── */}
      <div className="ctrb-form-header">
        <div>
          <span className="ctrb-status-badge">Review Pending</span>
          <h1 className="ctrb-form-title">Submit Contribution</h1>
          <p className="ctrb-form-sub">
            Help track new and updated darknet forums. Verified contributions earn leaderboard points.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {user?.role === "admin" && (
            <button
              className="ctrb-review-btn"
              onClick={() => setShowReviewPanel(true)}
            >
              Review Pending ({adminItems.length})
            </button>
          )}
        </div>
      </div>

      {/* ── Step Progress ── */}
      <StepProgress current={0} />

      {/* ── Two-column layout ── */}
      <div className="ctrb-grid">

        {/* LEFT: Form */}
        <div className="ctrb-form-card">

          {/* Forum Information */}
          <div className="ctrb-section">
            <h2 className="ctrb-section-title">Forum Information</h2>
            <div className="ctrb-row-2">
              <div className="ctrb-field">
                <label className="ctrb-label">Forum Name <span className="ctrb-required">*</span></label>
                <input
                  className={`ctrb-input${errors.forumName && forumName ? " ctrb-input-error" : ""}`}
                  placeholder="e.g. Darknet Alliance"
                  value={forumName}
                  onChange={(e) => setForumName(e.target.value)}
                />
                {errors.forumName && forumName && (
                  <span className="ctrb-error-msg">Forum name is required</span>
                )}
              </div>
              <div className="ctrb-field">
                <label className="ctrb-label">Primary Onion Link <span className="ctrb-required">*</span></label>
                <input
                  className={`ctrb-input ctrb-input-mono${errors.primaryLink && primaryLink ? " ctrb-input-error" : ""}`}
                  placeholder="http://example.onion"
                  value={primaryLink}
                  onChange={(e) => setPrimaryLink(e.target.value)}
                />
                {errors.primaryLink && primaryLink && (
                  <span className="ctrb-error-msg">Valid .onion URL required</span>
                )}
              </div>
            </div>
            <div className="ctrb-field">
              <label className="ctrb-label">Alternative / Mirror Links</label>
              <textarea
                className="ctrb-textarea"
                rows={3}
                placeholder="One link per line&#10;http://mirror1.onion&#10;http://mirror2.onion"
                value={mirrors}
                onChange={(e) => setMirrors(e.target.value)}
              />
            </div>
          </div>

          {/* Upload Zone */}
          <div className="ctrb-section">
            <h2 className="ctrb-section-title">Visual Evidence <span className="ctrb-optional">(Optional)</span></h2>
            <div
              className={`ctrb-upload-zone${dragOver ? " ctrb-upload-zone-hover" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); }}
            >
              <div className="ctrb-upload-icon-wrap">
                <UploadCloud size={28} className="ctrb-upload-icon" />
              </div>
              <div>
                <p className="ctrb-upload-title">Drag &amp; drop or click to upload</p>
                <p className="ctrb-upload-sub">PNG · JPG · Max 10 MB</p>
              </div>
            </div>
          </div>

          {/* Additional Context */}
          <div className="ctrb-section">
            <h2 className="ctrb-section-title">Additional Context</h2>
            <div className="ctrb-field">
              <textarea
                className="ctrb-textarea"
                rows={3}
                placeholder="Is login required? Are there captchas? Any specific access instructions?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Footer: note + submit */}
          <div className="ctrb-form-footer">
            <span className="ctrb-security-note">
              <Lock size={11} />
              Secure Submission — No IP logged
            </span>
            <button
              className={`ctrb-submit-btn${!isFormValid ? " ctrb-submit-btn-disabled" : ""}`}
              disabled={!isFormValid}
            >
              Submit Contribution
            </button>
          </div>
        </div>

        {/* RIGHT: Info Sidebar */}
        <div className="ctrb-sidebar">
          <InfoCard
            title="Why Contribute?"
            icon={<Share2 size={15} />}
            color="#6366F1"
            items={[
              "Discover new darknet forums",
              "Keep forum catalog updated",
              "Improve threat intelligence",
              "Earn contributor points",
            ]}
          />
          <InfoCard
            title="Submission Guidelines"
            icon={<Info size={15} />}
            color="#22c55e"
            items={[
              "Verify source links are active",
              "Include screenshots if possible",
              "Note login requirements",
              "Check for duplicates first",
            ]}
          />
        </div>
      </div>
    </div>
  );
}
